import React from 'react'
import { shallow } from 'zustand/shallow'
import { useAuth0 } from '@auth0/auth0-react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import SearchBar from 'material-ui-search-bar'

import Layout from '../Layout/Layout'
import NominaTable from './NominaTable'

import {
  getCompanyEmployees,
  getCompanyEmployeesBySearchTerm,
} from '../../services/CompaniesService'

import { useEmpresaStore } from '../../store/empresaStore'

import { CompanyType, NominaRowsType } from '../../types'

const Nomina = () => {
  const { t } = useTranslation('nomina')
  const [searched, setSearched] = React.useState<string>('') // texto actual del input
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [payroll, setPayroll] = React.useState<NominaRowsType[]>([]) // empleados actuales en vista
  const [rowCountState, setRowCountState] = React.useState(0)
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(25)
  const [isSearching, setIsSearching] = React.useState<boolean>(false)

  const { getAccessTokenSilently } = useAuth0()
  const { empresaId, selectedEmpresa } = useEmpresaStore(
    state => ({
      empresaId: state.empresaId,
      selectedEmpresa: state.selectedEmpresa,
    }),
    shallow,
  )

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const requestSearch = async (searchedVal: string) => {
    setSearched(searchedVal)
    setPage(0)

    if (searchedVal.length > 2) {
      setIsSearching(true)
    } else {
      setIsSearching(false)
    }
  }

  const cancelSearch = () => {
    setSearched('')
    setIsSearching(false)
    setPage(0)
  }

  const fetchEmployees = async () => {
    setIsLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const companyData = selectedEmpresa as CompanyType
      const payrollConfig = companyData?.payrollConfig
      const structureTypeId = payrollConfig?.structureTypeId
      const structureId = payrollConfig?.structureId

      const response =
        isSearching && searched.length > 2
          ? await getCompanyEmployeesBySearchTerm(
              token,
              empresaId,
              page,
              searched,
              structureTypeId,
              structureId,
            )
          : await getCompanyEmployees(
              token,
              empresaId,
              page,
              structureTypeId,
              structureId,
            )

      setPayroll(response.data.paginationList)
      setRowCountState(response.data.quantity)
    } catch (error) {
      console.error('Error obteniendo empleados:', error)
      setPayroll([])
    }
    setIsLoading(false)
  }

  React.useEffect(() => {
    fetchEmployees()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, page, rowsPerPage, isSearching, searched, selectedEmpresa])

  return (
    <Layout icon="PeopleAltOutlinedIcon" title={t('layoutTitle')}>
      <Box
        sx={{
          height: '100%',
          '& .theme--header': {
            backgroundColor: '#EEF1FC',
          },
        }}
      >
        <Grid container sx={{ py: '24px', placeItems: 'center' }}>
          <Grid display="flex" sx={{ placeItems: 'center' }} item xs={6}>
            <Box sx={{ marginRight: 4, width: '459px' }}>
              <SearchBar
                value={searched}
                onChange={requestSearch}
                onCancelSearch={cancelSearch}
                placeholder={t('searchPlaceholder')}
                style={{ boxShadow: 'none' }}
              />
            </Box>
          </Grid>
        </Grid>

        <NominaTable
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          isLoading={isLoading}
          page={page}
          payroll={payroll}
          rowCountState={rowCountState}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
        />
      </Box>
    </Layout>
  )
}

export default Nomina
